/**
 * The wall layer.
 *
 * Walls are per-tile, and there can be a thousand of them, so they are not
 * entities and they do not get sprites: the whole layer is one retained
 * `Graphics` rebuilt when the set changes (twice a second at most — that is how
 * often the server sends it). Every segment is drawn from its **connection
 * mask**, the four orthogonal neighbours that also carry the same kingdom's
 * wall, so a drawn line joins into one continuous rampart with corners and ends
 * instead of a row of unrelated blocks.
 *
 * Each people builds differently, because a wall is the most visible thing a
 * kingdom ever puts on the map: humans quarry pale limestone with square
 * battlements, dwarves stack dark granite seamed with lit runes, elves grow a
 * living palisade of timber and leaf.
 */

import { Graphics } from 'pixi.js';
import { ART, MAP_W, Race, WallKind } from '../../shared/constants';
import { state } from '../state';

const S = ART;

interface WallStyle {
  /** the shadow the stonework casts, drawn a little proud of it */
  dark: number;
  /** the body of the wall */
  core: number;
  /** the lit top edge, which is what makes it read as a wall and not a line */
  cap: number;
  /** the flourish that says whose hands built it */
  accent: number;
  /** half-thickness as a fraction of a tile */
  thickness: number;
}

export const WALL_STYLES: WallStyle[] = [
  /* humans — quarried limestone, square battlements */
  { dark: 0x413c33, core: 0xa79f8c, cap: 0xdcd6c4, accent: 0xdcd6c4, thickness: 0.30 },
  /* dwarves — dark granite, heavier, seamed with lit runes */
  { dark: 0x272b34, core: 0x757c8b, cap: 0xa9b0bf, accent: 0x7ad7ff, thickness: 0.35 },
  /* elves — a living palisade, grown rather than built */
  { dark: 0x241a12, core: 0x6b5138, cap: 0xb8834f, accent: 0x4f9e4a, thickness: 0.25 },
];

const styleFor = (race: number): WallStyle => WALL_STYLES[race] || WALL_STYLES[Race.Human];

/**
 * Queues the hub-and-arms outline of one segment. `t` is the half-thickness and
 * `dy` shifts the whole band, which is how the lit cap is drawn: the same shape
 * again, thinner and one notch higher.
 */
function band(g: Graphics, x0: number, y0: number, mask: number, t: number, dy: number) {
  const half = S / 2;
  const cx = x0 + half;
  const cy = y0 + half + dy;
  g.rect(cx - t, cy - t, t * 2, t * 2);
  if (mask & 1) g.rect(cx - t, y0 + dy, t * 2, half);        // north
  if (mask & 2) g.rect(cx - t, cy, t * 2, half);             // south
  if (mask & 4) g.rect(x0, cy - t, half, t * 2);             // west
  if (mask & 8) g.rect(cx, cy - t, half, t * 2);             // east
}

export class WallLayer {
  readonly graphics = new Graphics();
  private drawnRevision = -1;
  private drawnDrag = -1;
  private drawnMode = -1;

  constructor() {
    this.graphics.eventMode = 'none';
  }

  update() {
    // the layer changes twice a second at most; the drag preview changes with
    // the cursor, so both are watched
    if (this.drawnRevision === state.wallRevision
      && this.drawnDrag === state.wallDrag.length
      && this.drawnMode === state.wallMode) return;
    this.drawnRevision = state.wallRevision;
    this.drawnDrag = state.wallDrag.length;
    this.drawnMode = state.wallMode;
    this.redraw();
  }

  private redraw() {
    const g = this.graphics;
    g.clear();

    for (const w of state.walls.values()) {
      const owner = state.players.get(w.owner);
      const style = styleFor(owner ? owner.race : Race.Human);
      const x0 = (w.tile % MAP_W) * S;
      const y0 = ((w.tile / MAP_W) | 0) * S;
      const mask = this.maskFor(w.tile, w.owner);
      // a battered wall fades and darkens rather than shrinking: the line has to
      // stay legible right up to the moment it falls
      const hp = w.hp / 255;
      const a = 0.55 + 0.45 * hp;
      const t = S * style.thickness;

      band(g, x0, y0, mask, t + 0.4, 0.4);
      g.fill({ color: style.dark, alpha: a * 0.9 });
      band(g, x0, y0, mask, t, 0);
      g.fill({ color: style.core, alpha: a });
      band(g, x0, y0, mask, t * 0.5, -t * 0.55);
      g.fill({ color: style.cap, alpha: a * 0.8 });

      const cx = x0 + S / 2, cy = y0 + S / 2;
      if (w.kind === WallKind.Gate) {
        // a doorway, banded in the colours of whoever may walk through it
        g.rect(cx - t * 0.75, cy - t * 0.5, t * 1.5, t * 1.5)
          .fill({ color: 0x0d0a07, alpha: a });
        g.rect(cx - t * 0.9, cy - t * 0.95, t * 1.8, t * 0.45)
          .fill({ color: owner ? owner.color : 0xffd85e, alpha: a });
      } else if (owner?.race === Race.Dwarf) {
        g.rect(cx - 0.35, cy - 0.35, 0.7, 0.7).fill({ color: style.accent, alpha: a * 0.85 });
      } else if (owner?.race === Race.Elf) {
        g.rect(cx - t * 0.9, cy - t * 0.9, 0.6, 0.6)
          .rect(cx + t * 0.3, cy + t * 0.2, 0.6, 0.6)
          .fill({ color: style.accent, alpha: a * 0.9 });
      } else {
        // battlements: two notches on the parapet
        g.rect(cx - t * 0.85, cy - t - 0.7, t * 0.6, 0.7)
          .rect(cx + t * 0.25, cy - t - 0.7, t * 0.6, 0.7)
          .fill({ color: style.cap, alpha: a * 0.85 });
      }
    }

    this.drawDragPreview(g);
  }

  /**
   * The line you are currently drawing, before it is paid for. Drawn in the
   * same shape as the real thing so what you see is what you will get — but
   * hollow, because none of it exists yet.
   */
  private drawDragPreview(g: Graphics) {
    if (state.wallMode === 0 || state.wallDrag.length === 0) return;
    const me = state.players.get(state.you);
    const style = styleFor(me ? me.race : Race.Human);
    const set = new Set(state.wallDrag);
    for (const tile of state.wallDrag) {
      const x0 = (tile % MAP_W) * S;
      const y0 = ((tile / MAP_W) | 0) * S;
      let mask = 0;
      const x = tile % MAP_W, y = (tile / MAP_W) | 0;
      if (y > 0 && (set.has(tile - MAP_W) || state.walls.has(tile - MAP_W))) mask |= 1;
      if (set.has(tile + MAP_W) || state.walls.has(tile + MAP_W)) mask |= 2;
      if (x > 0 && (set.has(tile - 1) || state.walls.has(tile - 1))) mask |= 4;
      if (set.has(tile + 1) || state.walls.has(tile + 1)) mask |= 8;
      band(g, x0, y0, mask, S * style.thickness, 0);
      g.fill({ color: style.cap, alpha: 0.28 });
      band(g, x0, y0, mask, S * style.thickness, 0);
      g.stroke({ width: 0.35, color: state.wallMode === WallKind.Gate ? 0xffd85e : style.cap, alpha: 0.8 });
    }
  }

  /** Which orthogonal neighbours carry the same kingdom's wall. */
  private maskFor(tile: number, owner: number): number {
    const x = tile % MAP_W, y = (tile / MAP_W) | 0;
    let mask = 0;
    if (y > 0 && state.walls.get(tile - MAP_W)?.owner === owner) mask |= 1;
    if (state.walls.get(tile + MAP_W)?.owner === owner) mask |= 2;
    if (x > 0 && state.walls.get(tile - 1)?.owner === owner) mask |= 4;
    if (x < MAP_W - 1 && state.walls.get(tile + 1)?.owner === owner) mask |= 8;
    return mask;
  }
}
